import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../components/ui/Badge';
import {
  ChevronDownIcon,
  CogIcon,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAccountVesselScope } from '../../hooks/useAccountVesselScope';
import { getListReturnPath, openRecord } from '../../hooks/useNavigateBack';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import { normalizePaginatedListResponse } from '../../services/paginatedList';
import { deleteMgaPeriod } from '../../services/mutations';
import {
  closeApprovalBadgeVariant,
  formatMgaCloseApprovalStatus,
  formatMgaStatus,
  moduleConfig,
  statusBadgeVariant,
} from './config';
import { useMgaPeriods, type MgaPeriod, type MgaPeriodStatus } from './useData';

type StatusTab = 'all' | MgaPeriodStatus;

type VesselOption = { id: number; vesselName: string; vesselAbrv?: string | null };

type MgaListFilters = {
  view: StatusTab;
  search: string;
  year: string;
  vesselId: string;
};

const formatAmount = (value?: number | null) => {
  if (value == null || Number.isNaN(Number(value))) return '';
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const yearOptions = (() => {
  const y = new Date().getUTCFullYear();
  const opts: Array<{ value: string; label: string }> = [];
  for (let i = y + 1; i >= y - 5; i -= 1) opts.push({ value: String(i), label: String(i) });
  return opts;
})();

const viewTabs: Array<{
  id: StatusTab;
  label: string;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'all',
    label: 'All',
    emptyTitle: 'No MGA periods yet',
    emptyHint: 'Create an MGA period for a vessel and month.',
  },
  {
    id: 'DRAFT',
    label: 'Draft',
    emptyTitle: 'No draft periods',
    emptyHint: 'Draft MGA periods will show up here.',
  },
  {
    id: 'OPEN',
    label: 'Open',
    emptyTitle: 'No open periods',
    emptyHint: 'Open MGA periods for the selected year appear here.',
  },
  {
    id: 'CLOSED',
    label: 'Closed',
    emptyTitle: 'No closed periods',
    emptyHint: 'Closed MGA periods for the selected year appear here.',
  },
];

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const listReturnPath = getListReturnPath(location);
  const confirm = useConfirm();
  const lockedVesselId = useAccountVesselScope();

  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'mga';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const storageKey = getListStateStorageKey(moduleConfig.module.route.list);
  const [persisted, setPersisted] = usePersistedListFilters<MgaListFilters>(storageKey);

  const [view, setView] = useState<StatusTab>(() => persisted?.view ?? 'OPEN');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState(() => persisted?.search ?? '');
  const [search, setSearch] = useState(() => persisted?.search ?? '');
  const [year, setYear] = useState<string>(() => persisted?.year ?? String(new Date().getUTCFullYear()));
  const [vesselId, setVesselId] = useState<string>(() => persisted?.vesselId ?? '');

  useEffect(() => {
    setPersisted({ view, search, year, vesselId });
  }, [view, search, year, vesselId, setPersisted]);

  const openPeriod = (id: number) => {
    openRecord(navigate, moduleConfig.module.route.detail(id), listReturnPath);
  };

  const { data: vesselsRaw } = useSWR(
    lockedVesselId == null ? '/vessels?page=1&limit=500' : null,
    fetcher,
    { revalidateOnFocus: false },
  );
  const vessels = useMemo(() => {
    const list = normalizePaginatedListResponse<VesselOption>(vesselsRaw, 1, 500).data;
    return list.filter((v) => (v as VesselOption & { isActive?: boolean }).isActive !== false);
  }, [vesselsRaw]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, view, year, vesselId, itemsPerPage]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'periodFrom', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'vessel.vesselName' || key === 'status' ? 'asc' : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const listOptions = useMemo(() => {
    const filters: Record<string, string[]> = {};
    if (view !== 'all') filters.status = [view];
    if (year) filters.year = [year];
    if (lockedVesselId == null && vesselId) filters.vesselId = [vesselId];
    const hasListFilters = Object.keys(filters).length > 0;
    const sortKey =
      sortConfig.key === 'vessel.vesselName'
        ? 'vesselId'
        : sortConfig.key === 'updatedAt'
          ? 'periodFrom'
          : sortConfig.key;
    return {
      sortKey,
      sortDirection: sortConfig.direction,
      filters: hasListFilters ? { filters } : undefined,
    };
  }, [view, year, vesselId, lockedVesselId, sortConfig]);

  const { data, isLoading, total, mutate } = useMgaPeriods(
    search || undefined,
    page,
    itemsPerPage,
    listOptions,
  );

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasFilters = Boolean(
    search.trim() || year || (lockedVesselId == null && vesselId) || view !== 'all',
  );

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setYear('');
    setVesselId('');
    setView('all');
  };

  const handleDelete = async (e: React.MouseEvent, item: MgaPeriod) => {
    e.stopPropagation();
    const vessel = item.vessel?.vesselName ?? `Vessel #${item.vesselId}`;
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete MGA period for “${vessel}”?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMgaPeriod(item.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || moduleConfig.delete.errorMessage);
    }
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === view) ?? viewTabs[0], [view]);
  const formatDate = moduleConfig.utils.formatDate;

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label }) => ({ id, label }))}
      activeTab={view}
      onTabChange={(value) => setView(value as StatusTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      tabTrailing={
        <>
          <span
            className={cn('mx-1 my-1.5 w-px shrink-0 self-stretch', colors.border.replace('border-', 'bg-'))}
            aria-hidden
          />
          <label
            className={cn(
              'relative inline-flex items-center rounded-lg transition',
              year
                ? cn(colors.surface, 'shadow-sm ring-1', colors.primaryBorderAll)
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Filter by year</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none focus:ring-2 focus:ring-offset-0',
                colors.primaryBorderAll.replace('border-', 'focus:ring-'),
                year ? colors.text : colors.textMuted,
              )}
              title="Filter by year"
            >
              <option value="">All years</option>
              {yearOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                year ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
          {lockedVesselId == null && (
            <label
              className={cn(
                'relative inline-flex max-w-[12rem] items-center rounded-lg transition',
                vesselId
                  ? cn(colors.surface, 'shadow-sm ring-1', colors.primaryBorderAll)
                  : colors.interactiveHover,
              )}
            >
              <span className="sr-only">Filter by vessel</span>
              <select
                value={vesselId}
                onChange={(e) => setVesselId(e.target.value)}
                className={cn(
                  'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none truncate max-w-[11rem] focus:ring-2 focus:ring-offset-0',
                  colors.primaryBorderAll.replace('border-', 'focus:ring-'),
                  vesselId ? colors.text : colors.textMuted,
                )}
                title="Filter by vessel"
              >
                <option value="">All vessels</option>
                {vessels.map((v) => (
                  <option key={v.id} value={String(v.id)}>
                    {v.vesselName}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                className={cn(
                  'pointer-events-none absolute right-2 h-3.5 w-3.5',
                  vesselId ? colors.primaryFg : 'opacity-50',
                )}
              />
            </label>
          )}
        </>
      }
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: moduleConfig.search.placeholder,
        title: 'Searches vessel name, notes, and currency',
      }}
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/mga/settings/posting"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium',
              colors.border,
              colors.text,
            )}
            title="Posting settings"
          >
            <CogIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Posting</span>
          </Link>
          <Link
            to={moduleConfig.module.route.new}
            state={{ from: listReturnPath }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New period
          </Link>
        </div>
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading MGA periods…"
      isEmpty={data.length === 0}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: hasFilters ? 'No matching periods' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to={moduleConfig.module.route.new}
            state={{ from: listReturnPath }}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New period
          </Link>
        ),
      }}
      pagination={{
        page,
        totalPages,
        total: total ?? 0,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className={cn('sticky top-0 z-10 w-[5.5rem] font-medium', colors.surface)} />
            <GeneralListSortableTh sortKey="vessel.vesselName">Vessel</GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden w-20 font-medium sm:table-cell', colors.surface, colors.textMuted)}>
              Code
            </th>
            <GeneralListSortableTh sortKey="periodFrom" className="hidden w-28 md:table-cell">
              From
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden w-28 font-medium md:table-cell', colors.surface, colors.textMuted)}>
              To
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-16 font-medium lg:table-cell', colors.surface, colors.textMuted)}>
              Curr.
            </th>
            <GeneralListSortableTh sortKey="status" className="w-28">
              Status
            </GeneralListSortableTh>
            <th className={cn('sticky top-0 z-10 hidden w-36 font-medium md:table-cell', colors.surface, colors.textMuted)}>
              Approval
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-16 font-medium text-right lg:table-cell', colors.surface, colors.textMuted)}>
              Crew
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-24 font-medium text-right xl:table-cell', colors.surface, colors.textMuted)}>
              Net due
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-24 font-medium text-right xl:table-cell', colors.surface, colors.textMuted)}>
              Cash
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-24 font-medium text-right xl:table-cell', colors.surface, colors.textMuted)}>
              Bonded
            </th>
            <th className={cn('sticky top-0 z-10 hidden w-20 font-medium text-right lg:table-cell', colors.surface, colors.textMuted)}>
              Requests
            </th>
            <GeneralListSortableTh sortKey="updatedAt" className="hidden w-28 lg:table-cell">
              Updated
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => {
            const crewCount = item._count?.lines ?? item.lines?.length ?? 0;
            const requestCount = item._count?.crewRequests ?? 0;
            return (
              <tr
                key={item.id}
                onClick={() => openPeriod(item.id)}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  colors.border,
                  colors.interactiveHover,
                )}
              >
                <td>
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={moduleConfig.module.route.detail(item.id)}
                      state={{ from: listReturnPath }}
                      title="Dashboard"
                      aria-label="Open MGA dashboard"
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    {canEdit && (
                      <Link
                        onClick={(e) => e.stopPropagation()}
                        to={moduleConfig.module.route.sheet(item.id)}
                        state={{ from: listReturnPath }}
                        title="Edit MGA sheet"
                        aria-label="Edit MGA sheet"
                        className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                    )}
                    {canDelete && item.status !== 'CLOSED' && (
                      <button
                        type="button"
                        title="Delete"
                        aria-label="Delete MGA period"
                        onClick={(e) => handleDelete(e, item)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          isDark ? 'text-red-400 hover:bg-red-950/40' : 'text-red-600 hover:bg-red-50',
                        )}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="max-w-0">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>
                      {item.vessel?.vesselName ?? `Vessel #${item.vesselId}`}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {formatDate(item.periodFrom)} → {formatDate(item.periodTo)}
                    </p>
                  </div>
                </td>
                <td className={cn('hidden truncate sm:table-cell', colors.textMuted)}>
                  {item.vessel?.vesselAbrv || ''}
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {formatDate(item.periodFrom)}
                </td>
                <td className={cn('hidden whitespace-nowrap md:table-cell', colors.textMuted)}>
                  {formatDate(item.periodTo)}
                </td>
                <td className={cn('hidden truncate lg:table-cell', colors.textMuted)}>
                  {item.currency || ''}
                </td>
                <td className="whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    <Badge variant={statusBadgeVariant(item.status)} className="whitespace-nowrap w-fit">
                      {formatMgaStatus(item.status)}
                    </Badge>
                    <div className="flex flex-wrap gap-1 md:hidden">
                      {item.closeApprovalStatus ? (
                        <Badge
                          variant={closeApprovalBadgeVariant(item.closeApprovalStatus)}
                          className="text-[10px]"
                        >
                          {formatMgaCloseApprovalStatus(item.closeApprovalStatus)}
                          {item.closeApprovalStatus === 'PENDING' &&
                          (item.closeApprovalStepsTotal ?? 0) > 0
                            ? ` · ${item.closeApprovalStepsDone ?? 0}/${item.closeApprovalStepsTotal}`
                            : ''}
                        </Badge>
                      ) : item.status === 'OPEN' ? (
                        <span className={cn('text-[10px]', colors.textMuted)}>No close approval</span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(item.attachmentCount ?? item._count?.attachments ?? 0) > 0 ? (
                        <Badge variant="secondary" className="text-[10px]">
                          {item.attachmentCount ?? item._count?.attachments} file
                          {(item.attachmentCount ?? item._count?.attachments) === 1 ? '' : 's'}
                        </Badge>
                      ) : null}
                      {(item.missingWageSplitCount ?? 0) > 0 ? (
                        <Badge variant="danger" className="text-[10px]">
                          {item.missingWageSplitCount} wage gap
                          {item.missingWageSplitCount === 1 ? '' : 's'}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </td>
                <td className="hidden whitespace-nowrap md:table-cell">
                  {item.closeApprovalStatus ? (
                    <div className="flex flex-col gap-0.5">
                      <Badge
                        variant={closeApprovalBadgeVariant(item.closeApprovalStatus)}
                        className="w-fit whitespace-nowrap"
                      >
                        {formatMgaCloseApprovalStatus(item.closeApprovalStatus)}
                      </Badge>
                      {item.closeApprovalStatus === 'PENDING' &&
                      (item.closeApprovalStepsTotal ?? 0) > 0 ? (
                        <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>
                          Steps {item.closeApprovalStepsDone ?? 0}/{item.closeApprovalStepsTotal}
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span className={cn('text-xs', colors.textMuted)}>
                      {item.status === 'CLOSED' ? '—' : 'None'}
                    </span>
                  )}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums lg:table-cell', colors.text)}>
                  {crewCount}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums font-semibold xl:table-cell', colors.text)}>
                  {formatAmount(item.totals?.netDue)}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums xl:table-cell', colors.textMuted)}>
                  {formatAmount(item.cashOnHand)}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums xl:table-cell', colors.textMuted)}>
                  {formatAmount(item.bondedBalance)}
                </td>
                <td className={cn('hidden whitespace-nowrap text-right tabular-nums lg:table-cell', colors.textMuted)}>
                  {requestCount}
                </td>
                <td className={cn('hidden whitespace-nowrap lg:table-cell', colors.textMuted)}>
                  {formatDate(item.updatedAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
